import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { focusRing } from 'ui/a11y';

import { AdminPanel } from './admin-panel';
import { up } from './breakpoints';
import { AdminNavGroup, GUEST_PERMITS_NAV, STATISTICS_NAV } from './nav-items';
import { OverlayKind } from './use-header-model';

const Nav = styled.nav`
  display: none;
  order: 2; /* between the lockup (order 1) and the right cluster (order 3) in index.tsx's Bar */
  align-self: stretch;
  gap: 4px;
  margin-bottom: -2px; /* overlaps the bar's own 2px bottom border, matching each nav item */

  ${up('md')} {
    display: flex;
  }
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 3px 12px 0;
  border-bottom: 3px solid transparent;
  font-family: ${p => p.theme.font.family};
  font-size: 16px;
  font-weight: ${p => p.theme.font.weight.bold};
  white-space: nowrap;
  color: ${p => p.theme.colors.brown};
  text-decoration: none;

  &:hover {
    background: ${p => p.theme.colors.yellowPale};
  }

  &[aria-current='page'] {
    border-bottom-color: ${p => p.theme.colors.brown};
  }

  ${focusRing}
`;

export interface MainNavProps {
  label: string;
  showGuestPermits: boolean;
  showStatistics: boolean;
  adminGroups: AdminNavGroup[];
  adminActive: boolean;
  openOverlay: OverlayKind;
  onToggleAdmin: () => void;
  onCloseAdmin: () => void;
}

/**
 * The inline nav shown at md+ (`display:none` below that — see `Nav`'s own media query). Order
 * per spec: Külalisload, Administreerimine (a disclosure, `admin-panel.tsx`), Statistika.
 */
export function MainNav({
  label,
  showGuestPermits,
  showStatistics,
  adminGroups,
  adminActive,
  openOverlay,
  onToggleAdmin,
  onCloseAdmin,
}: MainNavProps) {
  const { t } = useTranslation();

  return (
    <Nav aria-label={label}>
      {showGuestPermits && (
        <NavItem to={GUEST_PERMITS_NAV.to}>{t(GUEST_PERMITS_NAV.labelKey)}</NavItem>
      )}
      {adminGroups.length > 0 && (
        <AdminPanel
          groups={adminGroups}
          active={adminActive}
          open={openOverlay === 'admin'}
          onToggle={onToggleAdmin}
          onClose={onCloseAdmin}
        />
      )}
      {showStatistics && <NavItem to={STATISTICS_NAV.to}>{t(STATISTICS_NAV.labelKey)}</NavItem>}
    </Nav>
  );
}

export default MainNav;
